/**
 * M8: diplomacy screen — relations, treaties, proposals and resource transfers.
 * Every button is a server request; the list always shows the server's answer.
 */
import { useCallback, useEffect, useState } from 'react';
import type { DiplomacyOverview, RelationView, ProposalType, TreatyType, ProposalView } from '@galaxis/shared';
import { DIPLOMACY_RULES } from '@galaxis/shared';
import {
  fetchDiplomacyOverview, proposeTreaty, answerProposal, cancelTreatyWith, sendResources, setDiplomacy,
} from '../api/galaxy.ts';
import { useGameStore } from '../store/game-store.ts';
import { GuideHint } from './guide/GuideHint.tsx';
import { ConfirmDialog } from './fleets/ConfirmDialog.tsx';
import { useLiveResources } from '../economy/live-store.ts';
import { NpcConversationPanel } from './NpcConversationPanel.tsx';

export const TREATY_LABEL: Record<TreatyType, string> = {
  non_aggression: 'Megnemtámadási egyezmény',
  alliance: 'Szövetség',
  trade: 'Kereskedelmi megállapodás',
};
export const PROPOSAL_LABEL: Record<ProposalType, string> = { ...TREATY_LABEL, peace: 'Békekötés' };
const PERSONALITY_LABEL = { expansionist: 'Terjeszkedő', militarist: 'Militarista', trader: 'Kereskedő' } as const;

function remaining(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'lejárt';
  const m = Math.ceil(ms / 60_000);
  return m >= 120 ? `${Math.round(m / 60)} óra` : `${m} perc`;
}

type Pending =
  | { kind: 'war'; rel: RelationView }
  | { kind: 'cancel'; rel: RelationView; type: TreatyType }
  | { kind: 'send'; rel: RelationView }
  | { kind: 'talk'; rel: RelationView };

function RelationCard({ rel, outgoing, busy, run, ask }: {
  rel: RelationView; outgoing: ProposalView[]; busy: boolean;
  run: (fn: () => Promise<unknown>, ok: string) => void; ask: (p: Pending) => void;
}) {
  const hostile = rel.status === 'hostile';
  const has = (t: TreatyType) => rel.treaties.some(x => x.type === t);
  const pendingTo = (t: ProposalType) => outgoing.some(p => p.toCivId === rel.civId && p.type === t);
  const btn = 'rounded-lg text-xs font-semibold px-3 min-h-[44px] border disabled:opacity-40';
  const propose = (t: ProposalType) => run(() => proposeTreaty(rel.civId, t), `Javaslat elküldve: ${PROPOSAL_LABEL[t]}`);

  return (
    <li className="sci-fi-panel rounded-xl p-4 space-y-3" data-testid="relation" data-civ-id={rel.civId}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: rel.civColor }} aria-hidden />
        <span className="text-slate-100 font-semibold">{rel.civName}</span>
        {rel.isNpc && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-300">
            NPC{rel.personality ? ` · ${PERSONALITY_LABEL[rel.personality]}` : ''}
          </span>
        )}
        {rel.protectedUntil && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" title="Újonc-védelem: nem támadható">
            🛡 védett még {remaining(rel.protectedUntil)}
          </span>
        )}
        <span className={`ml-auto text-xs font-medium ${hostile ? 'text-red-400' : 'text-slate-400'}`} data-testid="relation-status">
          {hostile ? '⚔️ Ellenséges' : '🤝 Semleges'}
        </span>
      </div>

      {rel.treaties.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {rel.treaties.map(t => (
            <li key={t.type} className="text-xs px-2 py-1 rounded-lg border border-accent/40 text-accent flex items-center gap-2" data-testid={`treaty-${t.type}`}>
              {TREATY_LABEL[t.type]}
              {t.endsAt ? <span className="text-amber-300">felmondva, még {remaining(t.endsAt)}</span> : (
                <button type="button" className="underline text-slate-400 min-h-[44px] px-2" disabled={busy}
                  onClick={() => ask({ kind: 'cancel', rel, type: t.type })}>felmondás</button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        {rel.isNpc && <button type="button" className={`${btn} border-violet-400/50 text-violet-300`} disabled={busy}
          onClick={() => ask({ kind: 'talk', rel })} data-testid="npc-talk">🗣 Tárgyalás</button>}
        {hostile ? (
          rel.hostileDeclaredByMe
            ? <button type="button" className={`${btn} border-slate-600 text-slate-200`} disabled={busy}
                onClick={() => run(() => setDiplomacy(rel.civId, 'neutral'), 'Hadüzenet visszavonva')}>Hadüzenet visszavonása</button>
            : <button type="button" className={`${btn} border-emerald-500/50 text-emerald-300`} disabled={busy || pendingTo('peace')}
                onClick={() => propose('peace')}>{pendingTo('peace') ? 'Békejavaslat elküldve' : 'Békejavaslat'}</button>
        ) : (
          <>
            {!has('trade') && <button type="button" className={`${btn} border-slate-600 text-slate-200`} disabled={busy || pendingTo('trade')}
              onClick={() => propose('trade')}>{pendingTo('trade') ? 'Kereskedelem: elküldve' : 'Kereskedelem'}</button>}
            {!has('non_aggression') && !has('alliance') && <button type="button" className={`${btn} border-slate-600 text-slate-200`} disabled={busy || pendingTo('non_aggression')}
              onClick={() => propose('non_aggression')}>{pendingTo('non_aggression') ? 'Megnemtámadási: elküldve' : 'Megnemtámadási egyezmény'}</button>}
            {!has('alliance') && <button type="button" className={`${btn} border-slate-600 text-slate-200`} disabled={busy || pendingTo('alliance')}
              onClick={() => propose('alliance')}>{pendingTo('alliance') ? 'Szövetség: elküldve' : 'Szövetség'}</button>}
            {(has('trade') || has('alliance')) && <button type="button" className={`${btn} border-amber-500/40 text-amber-300`} disabled={busy}
              onClick={() => ask({ kind: 'send', rel })}>Nyersanyag küldése</button>}
            {!has('non_aggression') && !has('alliance') && <button type="button" className={`${btn} border-red-500/40 text-red-300`} disabled={busy}
              onClick={() => ask({ kind: 'war', rel })}>Hadüzenet</button>}
          </>
        )}
      </div>
    </li>
  );
}

export function DiplomacyView() {
  const [ov, setOv] = useState<DiplomacyOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);
  const [send, setSend] = useState({ metal: 0, energy: 0 });
  const version = useGameStore(s => s.diplomacyVersion);
  const pushToast = useGameStore(s => s.pushToast);
  const live = useLiveResources();

  const load = useCallback(() => {
    fetchDiplomacyOverview().then(o => { setOv(o); setError(null); }).catch(e => setError(e instanceof Error ? e.message : 'Hiba'));
  }, []);
  useEffect(() => { load(); }, [load, version]);

  const run = (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    fn().then(() => { pushToast(ok); load(); })
      .catch(e => pushToast(e instanceof Error ? e.message : 'A művelet nem sikerült', 'error'))
      .finally(() => { setBusy(false); setPending(null); });
  };

  if (error) return <div className="flex-1 p-6 text-red-400 text-sm">{error}</div>;
  if (!ov) return <div className="flex-1 p-6 text-slate-500 text-sm">Diplomácia betöltése…</div>;

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="diplomacy-view">
      <div className="max-w-4xl mx-auto space-y-4">
        <GuideHint steps={['civTrace']} action={{ label: 'Vissza a térképre', onClick: () => useGameStore.getState().setActiveTab('galaxy') }} />
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <h1 className="text-lg font-semibold text-slate-200">Diplomácia</h1>
          {ov.myProtectedUntil && <span className="text-xs text-emerald-300">🛡 Újonc-védelem még {remaining(ov.myProtectedUntil)}</span>}
        </div>
        <p className="text-xs text-slate-500">
          Kereskedelmi bevétel: <span className="font-mono text-slate-300">+{ov.tradeIncomePerHour.metal / 60}/p fém, +{ov.tradeIncomePerHour.energy / 60}/p energia</span>
          {' · '}napi küldési keret: <span className="font-mono text-slate-300">{ov.transferAllowance.metal} fém / {ov.transferAllowance.energy} energia</span>
        </p>

        {ov.incoming.length > 0 && (
          <section className="sci-fi-panel rounded-xl p-4" data-testid="incoming-proposals">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Beérkezett javaslatok</h2>
            <ul className="space-y-2">
              {ov.incoming.map(p => (
                <li key={p.id} className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
                  <span className="flex-1 text-slate-200"><b>{p.fromCivName}</b>: {PROPOSAL_LABEL[p.type]} <span className="text-xs text-slate-500">({remaining(p.expiresAt)})</span></span>
                  <div className="flex gap-2">
                    <button type="button" className="btn-primary text-xs px-3" disabled={busy} onClick={() => run(() => answerProposal(p.id, 'accept'), 'Elfogadva')}>Elfogad</button>
                    <button type="button" className="btn-ghost text-xs px-3" disabled={busy} onClick={() => run(() => answerProposal(p.id, 'reject'), 'Elutasítva')}>Elutasít</button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {ov.outgoing.length > 0 && (
          <section className="sci-fi-panel rounded-xl p-4" data-testid="outgoing-proposals">
            <h2 className="text-sm font-semibold text-slate-300 mb-2">Elküldött javaslatok</h2>
            <ul className="space-y-2">
              {ov.outgoing.map(p => (
                <li key={p.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 text-slate-300">{p.toCivName}: {PROPOSAL_LABEL[p.type]} <span className="text-xs text-slate-500">({remaining(p.expiresAt)})</span></span>
                  <button type="button" className="btn-ghost text-xs px-3" disabled={busy} onClick={() => run(() => answerProposal(p.id, 'cancel'), 'Javaslat visszavonva')}>Visszavon</button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {ov.relations.length === 0 ? (
          <div className="sci-fi-panel rounded-xl p-6 text-center text-sm text-slate-500">
            Még nem találkoztál más civilizációval. Küldj felderítőt a szomszédos rendszerekbe!
          </div>
        ) : (
          <ul className="space-y-3">
            {ov.relations.map(rel => (
              <RelationCard key={rel.civId} rel={rel} outgoing={ov.outgoing} busy={busy} run={run} ask={p => { setSend({ metal: 0, energy: 0 }); setPending(p); }} />
            ))}
          </ul>
        )}
      </div>

      {pending?.kind === 'talk' && (
        <NpcConversationPanel rel={pending.rel} incoming={ov.incoming} onClose={() => setPending(null)} onChanged={load} />
      )}
      {pending?.kind === 'war' && (
        <ConfirmDialog title={`Hadüzenet: ${pending.rel.civName}`} confirmLabel="Hadüzenet" danger busy={busy}
          onCancel={() => setPending(null)} onConfirm={() => run(() => setDiplomacy(pending.rel.civId, 'hostile'), 'Hadüzenet elküldve')}>
          <p>Ellenséges viszonyban flottáitok harcolhatnak, és rendszereitek megtámadhatók. A kereskedelmi megállapodás azonnal megszűnik. Békét csak a másik fél beleegyezésével köthetsz.</p>
        </ConfirmDialog>
      )}
      {pending?.kind === 'cancel' && (
        <ConfirmDialog title={`${TREATY_LABEL[pending.type]} felmondása`} confirmLabel="Felmondás" danger busy={busy}
          onCancel={() => setPending(null)} onConfirm={() => run(() => cancelTreatyWith(pending.rel.civId, pending.type), 'Szerződés felmondva')}>
          <p>{pending.type === 'trade'
            ? 'A kereskedelmi bevétel azonnal megszűnik mindkét félnél.'
            : `A szerződés még ${Math.round(DIPLOMACY_RULES.treatyNoticeMs / 60_000)} percig érvényben marad (felmondási idő), utána szűnik meg.`}</p>
        </ConfirmDialog>
      )}
      {pending?.kind === 'send' && (
        <ConfirmDialog title={`Nyersanyag küldése: ${pending.rel.civName}`} confirmLabel="Küldés" busy={busy}
          onCancel={() => setPending(null)} onConfirm={() => run(() => sendResources(pending.rel.civId, send.metal, send.energy), 'Nyersanyag elküldve')}>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-400">Fém (van: {live?.metal ?? 0})
              <input type="number" min={0} max={ov.transferAllowance.metal} value={send.metal} inputMode="numeric"
                onChange={e => setSend(v => ({ ...v, metal: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))}
                className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-2 min-h-[44px] text-slate-100" aria-label="Küldendő fém" />
            </label>
            <label className="text-xs text-slate-400">Energia (van: {live?.energy ?? 0})
              <input type="number" min={0} max={ov.transferAllowance.energy} value={send.energy} inputMode="numeric"
                onChange={e => setSend(v => ({ ...v, energy: Math.max(0, Math.floor(Number(e.target.value) || 0)) }))}
                className="mt-1 w-full rounded-lg bg-slate-900 border border-slate-600 px-2 min-h-[44px] text-slate-100" aria-label="Küldendő energia" />
            </label>
          </div>
          <p className="text-xs text-slate-500">Napi keret: {ov.transferAllowance.metal} fém, {ov.transferAllowance.energy} energia.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
